/**
 * An import: a person's files written into the workspace's records by a server
 * job (docs/plans/setup-from-files.md, Decided 11 to 18). The transport shape
 * of the job, its plan and the rows it set aside, and the guards both ends
 * read a body with.
 *
 * The model writes one `ImportMapping` per sheet from the file's summary;
 * code applies it to every row (import-transform.ts), in the order the plan
 * puts the steps in (import-plan.ts).
 */
import type { EntryLinesWrite } from './api';

/* ------------------------------------------------------------------ status */

/** check: stop after preparing each step. auto: run every step without stopping. */
export const IMPORT_MODES = ['check', 'auto'] as const;
export type ImportMode = (typeof IMPORT_MODES)[number];

/**
 * Where a job stands. `planned` until started; `running` while a runner may
 * take it; `waiting` for the person in check mode; `paused`; `stopped` (ended
 * with what is in); `done`; `undone`.
 */
export const IMPORT_STATUSES = ['planned', 'running', 'waiting', 'paused', 'stopped', 'done', 'undone'] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

/** A job no runner will touch again. */
export const IMPORT_ENDED: readonly ImportStatus[] = ['stopped', 'done', 'undone'];

/** A job the sidebar marks: one still going, or one waiting for the person. */
export const IMPORT_ACTIVE: readonly ImportStatus[] = ['planned', 'running', 'waiting', 'paused'];

/** A list step makes the list items the mapped columns pick from; a record step writes a sheet's rows. */
export const IMPORT_STEP_KINDS = ['list', 'record'] as const;
export type ImportStepKind = (typeof IMPORT_STEP_KINDS)[number];

/**
 * Where one step stands.
 *
 *   pending       not prepared yet
 *   ready         prepared, waiting for Approve, Change or Skip (check mode)
 *   approved      to be written
 *   done          written
 *   skipped       the person skipped it, or the job stopped before it
 *   shape_needed  the sheet's layout fits no mapping; set aside whole (Decided 18)
 *   blocked       the file's own total disagrees with its rows; nothing is saved until the person chooses
 *   undone        its entries were taken out
 */
export const IMPORT_STEP_STATUSES = [
  'pending',
  'ready',
  'approved',
  'done',
  'skipped',
  'shape_needed',
  'blocked',
  'undone',
] as const;
export type ImportStepStatus = (typeof IMPORT_STEP_STATUSES)[number];

/** Why a row was set aside (Decided 15). */
export const ASIDE_REASONS = ['rules', 'unknown_name', 'bad_amount', 'bad_date', 'unsure', 'shape_needed'] as const;
export type AsideReason = (typeof ASIDE_REASONS)[number];

/** What became of a row set aside. */
export const ASIDE_STATUSES = ['open', 'accepted', 'fixed', 'left_out'] as const;
export type AsideStatus = (typeof ASIDE_STATUSES)[number];

/** Rows written per transaction. */
export const IMPORT_BATCH = 50;

/* ----------------------------------------------------------------- mapping */

/** How a date column is written, e.g. `DD-MM-YYYY`, `D/M/YY`, `YYYY-MM-DD`, `DD MMM YYYY`. */
export type DateFormat = string;

/**
 * What one column becomes (Decided 12). A fixed list: the model picks one per
 * column, code applies it.
 *
 *   date       a day, read in the format given
 *   number     a figure; Indian grouping (1,23,456.50) and brackets read
 *   money      a figure, a currency mark allowed; `Dr`/`Cr` read as the sign
 *   text       the words as written
 *   pick       a name picked from a list or a choice; `choices` maps a code
 *              in the file (`Cr`, `Dr`) to the choice's own name or id
 *   constant   the same value on every row, whatever the cell says
 *   leave_out  not saved
 */
export type ColumnTransform =
  | { readonly kind: 'date'; readonly format: DateFormat }
  | { readonly kind: 'number' }
  | { readonly kind: 'money' }
  | { readonly kind: 'text' }
  | { readonly kind: 'pick'; readonly choices?: Readonly<Record<string, string>> }
  /** A yes or no column. A cell that is neither leaves the field unset; the row is still saved. */
  | { readonly kind: 'boolean' }
  | { readonly kind: 'constant'; readonly value: string | number | boolean }
  | { readonly kind: 'leave_out' };

export const COLUMN_TRANSFORM_KINDS = ['date', 'number', 'money', 'text', 'pick', 'boolean', 'constant', 'leave_out'] as const;
export type ColumnTransformKind = (typeof COLUMN_TRANSFORM_KINDS)[number];

/**
 * Why a column fills no field, found by code (import-reasons.ts), never asked
 * of the model. Names are labels, as the person reads them.
 *
 *   computed      the record works it out (a formula field the header names)
 *   other_record  the header names a field of another record the person can fill
 *   private       left out as a bank account, card or Aadhaar number (SheetSummary.leftOut)
 *   no_field      none of the above
 */
export type ColumnReason =
  | { readonly kind: 'computed'; readonly field: string }
  | { readonly kind: 'other_record'; readonly record: string; readonly field: string }
  | { readonly kind: 'private' }
  | { readonly kind: 'no_field' };

export const COLUMN_REASON_KINDS = ['computed', 'other_record', 'private', 'no_field'] as const;
export type ColumnReasonKind = (typeof COLUMN_REASON_KINDS)[number];

/** One column of a sheet, and the field it fills. `column` is null only for a constant with no cell behind it. */
export type MappedColumn = {
  /** Zero-based, in the sheet as read (ColumnProfile.index). */
  readonly column: number | null;
  /** The header's words, for the mapping table. */
  readonly header: string;
  /** The field it fills; null when it is left out. */
  readonly fieldId: string | null;
  readonly transform: ColumnTransform;
  /** Why it fills no field, set by the server only when `fieldId` is null. Absent on a mapping kept before reasons were. */
  readonly reason?: ColumnReason;
};

/** A column of the sheet the mapping does not list (the answer left it out, or it is private), and why. */
export type NotKeptColumn = { readonly column: number; readonly header: string; readonly reason: ColumnReason };

/**
 * How rows of a sheet come together as the lines of one entry:
 *
 *   column  rows next to each other that share the column's cell (the bill's
 *           number) are one entry; a blank cell there carries on the entry
 *           above it, as a register writes a bill's number on its first line
 *   sheet   every row is a line of one entry (one bill's items, its date and
 *           its party given once, as constants)
 *
 * The entry's own fields are read from its first row.
 */
export type LinesGroup = { readonly kind: 'column'; readonly column: number; readonly header: string } | { readonly kind: 'sheet' };

/**
 * The line each row also makes, for a record that holds lines (an invoice and
 * its items): every row is one line, the line's fields read from their
 * columns the way the record's are. Without `group`, every row is also its
 * own entry; with it, the rows of one entry are its lines.
 */
export type MappedLines = {
  /** The lines record (kind lines) the line is an entry of. */
  readonly recordId: string;
  /** The record's `lines` field that holds the line. */
  readonly fieldId: string;
  readonly columns: readonly MappedColumn[];
  readonly group?: LinesGroup;
};

/** How one sheet's rows become entries of one record. */
export type ImportMapping = {
  readonly fileId: string;
  readonly sheet: string;
  /** The zero-based header row; null when the sheet has none. */
  readonly headerRow: number | null;
  /** Zero-based rows not to read, beyond the header and the rows above it (a totals row, a note). */
  readonly skipRows: readonly number[];
  readonly recordId: string;
  readonly columns: readonly MappedColumn[];
  /** The line each row makes, when one row is one line of the record's entry. */
  readonly lines?: MappedLines;
  /**
   * The sheet's columns the mapping does not list, the private ones among
   * them, each with its reason, so the map draws every column. Set by the
   * server; absent on a mapping kept before reasons were.
   */
  readonly notKept?: readonly NotKeptColumn[];
};

/* ------------------------------------------------------------------- shapes */

/**
 * A sheet in an unusual layout, read by a small program (Decided 18): a
 * column for each day, say, where a row for each entry was expected. The
 * model writes the program from the sheet's summary; code runs it over every
 * batch of rows. The program is never shown to the person: the API answers
 * the step without `js`, and the screen shows rows before and after.
 */
export type ShapeProgram = {
  /** The transform: the body of an async function, run once per batch of rows. Kept on the server. */
  readonly js?: string;
  /**
   * What each entry the program makes holds, in order: the field, and how
   * the program's words for it are read. `column` is the place in the entry.
   */
  readonly columns: readonly MappedColumn[];
};

/** One entry a program made, as the screen shows it: the row of the file it came from, and its cells in the program's column order. */
export type ShapeEntry = { readonly row: number; readonly cells: readonly string[] };

/** Four rows of the file, and the entries they make (board import-step-shape). */
export type ShapeSample = {
  /** The file's header row, as written. */
  readonly header: readonly string[];
  /** Four rows of the file, each with its zero-based number. */
  readonly rows: readonly { readonly row: number; readonly cells: readonly string[] }[];
  /** The first entries those rows make, dates written YYYY-MM-DD and codes as the choices they mean. */
  readonly entries: readonly ShapeEntry[];
  /** Entries the four rows make in all. */
  readonly made: number;
};

/** Why a sheet's layout fits no mapping, and, when one was written, the program that reads it. */
export type ImportShape = {
  readonly fileId: string;
  readonly sheet: string;
  readonly why: string;
  readonly program?: ShapeProgram;
  /** Once the step is prepared. */
  readonly sample?: ShapeSample;
};

/** Entries one sheet row may make: an entry's number is its row's times this, plus its place. */
export const SHAPE_SPREAD = 1000;

/* -------------------------------------------------------------------- lists */

/** What OS will do with one name a picking column holds. */
export const LIST_NAME_DECISIONS = ['existing', 'new', 'ask'] as const;
export type ListNameDecision = (typeof LIST_NAME_DECISIONS)[number];

/** One name a list step found, and what it is to become. */
export type ListName = {
  readonly name: string;
  /** Rows that name it. */
  readonly rows: number;
  readonly decision: ListNameDecision;
  /** The existing item it is, or the near one asked about. */
  readonly match: { readonly entryId: string; readonly name: string } | null;
  /**
   * The first row that names it: what a new item made from it keeps as its
   * source. `sheet` tells two sheets of one workbook apart, so a name set
   * aside and the row that names it count as one row (Q4 of docs/plans/qa-fixes-1.md).
   */
  readonly from?: { readonly fileId: string; readonly row: number; readonly sheet?: string };
};

/**
 * Where a list step reads its names: a picking column of a record step's
 * sheet, or, for a sheet a program reads (`shaped`), a column of the entries
 * the program makes, read by running it over the sheet (Decided 12 and 18).
 */
export type ListSource = {
  readonly fileId: string;
  readonly sheet: string;
  /** The sheet's column; for a shaped source, the place in the program's entries. */
  readonly column: number;
  readonly headerRow: number | null;
  readonly skipRows: readonly number[];
  /** The field of the record step that picks from this list. */
  readonly fieldId: string;
  /** The names are what the sheet's program gives for `column`, not a column of the sheet. */
  readonly shaped?: true;
  /**
   * A constant's name (the supplier a bill's title rows give): counted once,
   * with no cell behind it, so `column` is -1 and no row is read for it.
   */
  readonly name?: string;
};

/* -------------------------------------------------------------------- steps */

/** A file that carries its own total, checked against its rows (Decided 15). */
export type TotalCheck = {
  readonly expected: number;
  readonly found: number;
  readonly agrees: boolean;
  /** The person chose to save the rows though the total disagrees. */
  readonly accepted?: boolean;
};

/** What preparing a step found, before anything is written. */
export type StepPreview = {
  readonly rows: number;
  /** Rows that will be saved. */
  readonly good: number;
  /** Rows already in the record: skipped. */
  readonly duplicates: number;
  readonly aside: number;
  /** Names to add to the list (a list step). */
  readonly newNames?: number;
  readonly total?: TotalCheck | null;
};

/** What writing a step has done so far. */
export type StepProgress = {
  /** Rows read so far, the next row to read being `next`. */
  readonly read: number;
  /** The zero-based sheet row to read next; resume continues here. */
  readonly next: number;
  readonly saved: number;
  readonly duplicates: number;
  readonly aside: number;
};

export type ImportStep = {
  readonly index: number;
  readonly kind: ImportStepKind;
  /** The record the step writes into: a list's own record for a list step. */
  readonly recordId: string;
  readonly recordName: string;
  readonly status: ImportStepStatus;
  /** A record step's mapping. Null for a list step, and for a sheet whose layout fits none. */
  readonly mapping: ImportMapping | null;
  /** A list step's sources. */
  readonly sources?: readonly ListSource[];
  /** A list step's names, once prepared. */
  readonly names?: readonly ListName[];
  /**
   * A list step's required fields, by name, that its new names come in
   * without: no file fills them, so they wait to be filled on each name, as
   * Build seeds a list's names (Q4 of docs/plans/qa-fixes-1.md).
   */
  readonly unfilled?: readonly string[];
  /**
   * A sheet whose layout fits no mapping: why, and the program that reads it
   * (pending, then ready with its sample), or none (shape_needed).
   */
  readonly shape?: ImportShape;
  readonly preview?: StepPreview;
  /**
   * The person said to save the step although its file's total disagrees,
   * with a changed mapping (Change with acceptTotal): the step, prepared
   * again, is not blocked on its total.
   */
  readonly totalAccepted?: boolean;
  readonly progress?: StepProgress;
  /** Rows in the sheet: what the progress counts towards. */
  readonly rows: number;
};

/**
 * What planning made of one sheet of the files, or of a file with no sheet to
 * read, so every one of them ends with an outcome the person sees.
 *
 *   brought_in  the sheet has a step
 *   set_aside   no record fits the sheet, or its layout fits no mapping and no program reads it;
 *               or a scan or a photo with pages read, whose lines are drafted in the chat, not
 *               imported (its reason says whether every page could be read)
 *   not_read    the file holds no table and is not a scan with pages read (a PDF of prose): it
 *               was read in the setup chat only
 */
export const SHEET_OUTCOMES = ['brought_in', 'set_aside', 'not_read'] as const;
export type SheetOutcomeKind = (typeof SHEET_OUTCOMES)[number];

export type SheetOutcome = {
  readonly fileId: string;
  /** The file's name, as the person gave it. */
  readonly file: string;
  /** Null for a file with no sheet read. */
  readonly sheet: string | null;
  readonly outcome: SheetOutcomeKind;
  /** The rows of the sheet's step (brought_in); null otherwise. */
  readonly entries: number | null;
  /** Why it was not brought in, in the model's words; null when it gave none, and the screen says the outcome's own words. */
  readonly reason: string | null;
};

/**
 * One sheet planning names first. A file with no sheet (a PDF with no table,
 * a scan, a photo, a workbook whose sheets read hold no table) is one row with
 * its sheet null. `skipped` is on every row of a workbook read in part, and on
 * no other: how many sheets past those read its summary names, none of them
 * read (a floor at WORKBOOK_SHEETS_NAMED), so a row never says the file was
 * read in the chat, and a plan with no step never says nothing in the files
 * fits.
 */
export type PlanProgressSheet = {
  readonly fileId: string;
  readonly file: string;
  readonly sheet: string | null;
  readonly rows: number;
  readonly skipped?: number;
};

/**
 * What planning reports as it goes (the `plan` event of a streamed POST
 * /v1/imports), so the person sees each sheet move: the sheets first (their
 * shape is known from the files); then a scan's pages the setup sitting left,
 * a group at a time as they are read (`reading` with its sheet null and the
 * pages, from 1); then each sheet when its mapping starts and when it ends. A
 * sheet ends `set_aside` when a record fits it but no mapping or program reads
 * its layout, and `none` when no record fits it at all.
 */
export type PlanProgress =
  | { readonly kind: 'sheets'; readonly sheets: readonly PlanProgressSheet[] }
  | { readonly kind: 'reading'; readonly fileId: string; readonly sheet: string }
  | { readonly kind: 'reading'; readonly fileId: string; readonly sheet: null; readonly pages: { readonly from: number; readonly to: number } }
  | { readonly kind: 'done'; readonly fileId: string; readonly sheet: string; readonly status: 'mapped' | 'program' | 'set_aside' | 'none'; readonly recordName: string | null };

export type ImportPlan = {
  readonly steps: readonly ImportStep[];
  /** Every sheet of the files and every file with none, in the files' order. Absent on a job planned before outcomes were kept. */
  readonly outcomes?: readonly SheetOutcome[];
};

/** The job's running totals. */
export type ImportCounters = {
  /** Rows the job will read across every record step. */
  readonly rows: number;
  readonly read: number;
  readonly saved: number;
  readonly duplicates: number;
  /** As the API answers it: the rows set aside that still wait for the person, open and of a step not undone. */
  readonly aside: number;
  /** When the writing began and when these were counted, for the rate. */
  readonly startedAt: string | null;
  readonly at: string | null;
};

export const NO_COUNTERS: ImportCounters = {
  rows: 0,
  read: 0,
  saved: 0,
  duplicates: 0,
  aside: 0,
  startedAt: null,
  at: null,
};

/** One job as the API answers it. */
export type ImportJob = {
  readonly id: string;
  readonly tenantId: string;
  readonly createdBy: string;
  readonly files: readonly string[];
  readonly mode: ImportMode;
  readonly status: ImportStatus;
  readonly plan: ImportPlan;
  readonly currentStep: number;
  readonly counters: ImportCounters;
  readonly error: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/* -------------------------------------------------------------------- aside */

/** A name OS would pick in place of the one the file has. */
export type AsideName = { readonly fieldId: string; readonly given: string; readonly entryId: string; readonly name: string };

/** What OS would save instead of a row it set aside. */
export type AsideSuggestion = {
  /** The values by field id, as they would be saved. */
  readonly values?: Readonly<Record<string, unknown>>;
  /** A name it would pick in place of the one the file has. */
  readonly name?: AsideName;
  /** What the record's rules said, in words. */
  readonly message?: string;
  /**
   * The line the row makes, in a step whose rows make lines: the `lines`
   * field that holds it, the values it read, and the name it would pick in
   * place of one its list does not hold. Saved with the entry, by Accept and
   * by Fix alike; a row of such a step without it has nothing whole to save.
   */
  readonly line?: { readonly fieldId: string; readonly values: Readonly<Record<string, unknown>>; readonly name?: AsideName };
  /**
   * In a step whose rows come together as one entry (`MappedLines.group`):
   * every line of the entry, one per row, each as `line` holds one, with the
   * row it came from and its cells. In place of `line`.
   */
  readonly lines?: readonly AsideLine[];
  /** The names of the list rows its values and lines point at, by row id: what a fix shows in their pickers. */
  readonly titles?: Readonly<Record<string, string>>;
};

/** One line of an entry set aside whose rows are its lines. */
export type AsideLine = {
  readonly fieldId: string;
  readonly values: Readonly<Record<string, unknown>>;
  readonly name?: AsideName;
  /** The zero-based row of the sheet the line was read from. */
  readonly row: number;
  /** The line's cells, by header. */
  readonly raw: Readonly<Record<string, string>>;
};

export type AsideRow = {
  readonly id: string;
  readonly step: number;
  readonly fileId: string;
  /** Null when a whole sheet was set aside. */
  readonly rowNumber: number | null;
  /** The row as the file had it, by header. */
  readonly raw: Readonly<Record<string, string>>;
  readonly reason: AsideReason;
  readonly suggestion: AsideSuggestion | null;
  readonly status: AsideStatus;
  readonly entryId: string | null;
};

/* ------------------------------------------------------------------- routes */

/** A sheet the import card showed going into a record: the plan maps it there and nowhere else. */
export type ImportTarget = { readonly fileId: string; readonly sheet: string; readonly recordId: string };

/** POST /v1/imports. `targets`: the record the card named for each sheet that had one (Decided 19). */
export type ImportCreateRequest = {
  readonly files: readonly string[];
  readonly mode?: ImportMode;
  readonly targets?: readonly ImportTarget[];
};

/** GET /v1/imports/:id and every action's answer: the job, and the current step's preview. */
export type ImportResponse = {
  readonly job: ImportJob;
  /** The step the job is on, with its rows set aside that are still open (at most 50). */
  readonly current: {
    readonly step: ImportStep;
    readonly aside: readonly AsideRow[];
  } | null;
};

/** GET /v1/imports/active: the jobs the sidebar marks. */
export type ImportActiveResponse = {
  readonly jobs: readonly Pick<ImportJob, 'id' | 'status' | 'mode' | 'currentStep' | 'counters' | 'updatedAt'>[];
};

/** POST /v1/imports/:id/approve, with the person's changes, if any. */
export type ImportApproveRequest = {
  readonly mapping?: ImportMapping;
  /** A list step's names, as the person left them: a name they said is new, or the item it is. */
  readonly names?: readonly ListName[];
  /** Save a blocked file's rows though its total disagrees. */
  readonly acceptTotal?: boolean;
};

/** POST /v1/imports/:id/mode. */
export type ImportModeRequest = { readonly mode: ImportMode };

/** GET /v1/imports/:id/aside. */
export type ImportAsideResponse = { readonly rows: readonly AsideRow[] };

export const ASIDE_ACTIONS = ['accept', 'fix', 'leave_out'] as const;
export type AsideAction = (typeof ASIDE_ACTIONS)[number];

/**
 * POST /v1/imports/:id/aside/:rowId. `values` with fix; `lines` with a fix
 * made in the entry form, every run of lines as the form sends a create:
 * saved in place of the lines OS read.
 */
export type AsideActionRequest = {
  readonly action: AsideAction;
  readonly values?: Readonly<Record<string, unknown>>;
  readonly lines?: EntryLinesWrite;
};

/** What an aside action, or accept-all, saved. */
export type AsideActionResponse = { readonly saved: number; readonly refused: number; readonly rows: readonly AsideRow[] };

/**
 * GET /v1/records/:recordId/entries/:entryId/source: where an imported entry
 * came from (Decided 13). `row` is the zero-based row of its sheet, as the
 * entry's source_row keeps it; `fileName` is null for anyone but an admin
 * (Decided 20), and for a file removed since. Null for an entry no import wrote.
 */
export type EntrySourceResponse = {
  readonly source: {
    readonly importJobId: string;
    readonly fileId: string;
    readonly fileName: string | null;
    readonly row: number;
  } | null;
};

/* ------------------------------------------------------------------- guards */

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const oneOf =
  <T extends string>(list: readonly T[]) =>
  (value: unknown): value is T =>
    typeof value === 'string' && (list as readonly string[]).includes(value);

export const isImportMode = oneOf(IMPORT_MODES);
export const isImportStatus = oneOf(IMPORT_STATUSES);
export const isImportStepStatus = oneOf(IMPORT_STEP_STATUSES);
export const isAsideReason = oneOf(ASIDE_REASONS);
export const isAsideStatus = oneOf(ASIDE_STATUSES);
export const isAsideAction = oneOf(ASIDE_ACTIONS);

/** Tokens a date format may use, between separators. */
const DATE_TOKENS = /^(D|DD|M|MM|MMM|YY|YYYY)$/;

/** Whether a date format is one `parseDate` reads: day, month and year tokens, each once, apart by `-`, `/`, `.` or a space. */
export const isDateFormat = (value: unknown): value is DateFormat => {
  if (typeof value !== 'string' || value.length > 20) return false;
  const tokens = value.split(/[-/. ]+/);
  if (tokens.length !== 3 || !tokens.every((token) => DATE_TOKENS.test(token))) return false;
  const kinds = tokens.map((token) => token[0]).sort().join('');
  return kinds === 'DMY';
};

export const isColumnTransform = (value: unknown): value is ColumnTransform => {
  if (!isObject(value)) return false;
  switch (value.kind) {
    case 'date':
      return isDateFormat(value.format);
    case 'number':
    case 'money':
    case 'text':
    case 'boolean':
    case 'leave_out':
      return true;
    case 'pick':
      return (
        value.choices === undefined ||
        (isObject(value.choices) && Object.values(value.choices).every((one) => typeof one === 'string'))
      );
    case 'constant':
      return typeof value.value === 'string' || typeof value.value === 'boolean' || (typeof value.value === 'number' && Number.isFinite(value.value));
    default:
      return false;
  }
};

const isRowNumber = (value: unknown): value is number => Number.isInteger(value) && (value as number) >= 0;

export const isColumnReason = (value: unknown): value is ColumnReason => {
  if (!isObject(value)) return false;
  switch (value.kind) {
    case 'computed':
      return typeof value.field === 'string';
    case 'other_record':
      return typeof value.record === 'string' && typeof value.field === 'string';
    case 'private':
    case 'no_field':
      return true;
    default:
      return false;
  }
};

const isNotKeptColumn = (value: unknown): value is NotKeptColumn =>
  isObject(value) && isRowNumber(value.column) && typeof value.header === 'string' && isColumnReason(value.reason);

export const isMappedColumn = (value: unknown): value is MappedColumn => {
  if (!isObject(value)) return false;
  if (!(value.column === null || isRowNumber(value.column))) return false;
  if (typeof value.header !== 'string') return false;
  if (!(value.fieldId === null || typeof value.fieldId === 'string')) return false;
  if (!isColumnTransform(value.transform)) return false;
  if (value.reason !== undefined && !isColumnReason(value.reason)) return false;
  // Only a constant stands with no cell behind it.
  if (value.column === null && value.transform.kind !== 'constant') return false;
  return true;
};

const isLinesGroup = (value: unknown): value is LinesGroup =>
  isObject(value) && (value.kind === 'sheet' || (value.kind === 'column' && isRowNumber(value.column) && typeof value.header === 'string'));

const isMappedLines = (value: unknown): value is MappedLines =>
  isObject(value) &&
  typeof value.recordId === 'string' &&
  typeof value.fieldId === 'string' &&
  Array.isArray(value.columns) &&
  value.columns.every(isMappedColumn) &&
  (value.group === undefined || isLinesGroup(value.group));

export const isImportMapping = (value: unknown): value is ImportMapping =>
  isObject(value) &&
  typeof value.fileId === 'string' &&
  typeof value.sheet === 'string' &&
  (value.headerRow === null || isRowNumber(value.headerRow)) &&
  Array.isArray(value.skipRows) &&
  value.skipRows.every(isRowNumber) &&
  typeof value.recordId === 'string' &&
  Array.isArray(value.columns) &&
  value.columns.every(isMappedColumn) &&
  (value.lines === undefined || isMappedLines(value.lines)) &&
  (value.notKept === undefined || (Array.isArray(value.notKept) && value.notKept.every(isNotKeptColumn)));

export const isListName = (value: unknown): value is ListName =>
  isObject(value) &&
  typeof value.name === 'string' &&
  Number.isInteger(value.rows) &&
  oneOf(LIST_NAME_DECISIONS)(value.decision) &&
  (value.match === null ||
    (isObject(value.match) && typeof value.match.entryId === 'string' && typeof value.match.name === 'string'));

/** The body of an approve, read; null when it is not one. */
export const readApproveRequest = (body: unknown): ImportApproveRequest | null => {
  if (body === undefined || body === null) return {};
  if (!isObject(body)) return null;
  if (body.mapping !== undefined && !isImportMapping(body.mapping)) return null;
  if (body.names !== undefined && !(Array.isArray(body.names) && body.names.every(isListName))) return null;
  if (body.acceptTotal !== undefined && typeof body.acceptTotal !== 'boolean') return null;
  return body as ImportApproveRequest;
};
